import React, { useState, useEffect } from 'react';
import { getHeroSlides } from '../services/api';

const DynamicHero = ({ onContactClick }) => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    fetchHeroSlides();
  }, []);

  useEffect(() => {
    let interval;
    if (autoplay && slides.length > 1) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [autoplay, slides.length]);

  const fetchHeroSlides = async () => {
    try {
      const response = await getHeroSlides();
      console.log('Fetched slides:', response.data);
      setSlides(response.data || []);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
      setSlides([]);
    } finally {
      setLoading(false);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setAutoplay(false);
    setTimeout(() => setAutoplay(true), 5000);
  };

  const nextSlide = () => {
    if (slides.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % slides.length);
    setAutoplay(false);
    setTimeout(() => setAutoplay(true), 5000);
  };

  const prevSlide = () => {
    if (slides.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    setAutoplay(false);
    setTimeout(() => setAutoplay(true), 5000);
  };

  // Better URL handling for S3 images
  const getFullImageUrl = (url) => {
    if (!url) return '';
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    if (url.startsWith('/uploads')) {
      return `http://localhost:5000${url}`;
    }
    
    if (url.includes('sandeep-bucketnew') || url.includes('amazonaws.com')) {
      if (url.startsWith('https://sandeep-bucketnew.s3.eu-north-1.amazonaws.com')) {
        return url;
      }
      return `https://sandeep-bucketnew.s3.eu-north-1.amazonaws.com/${url}`;
    }
    
    return url;
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length]);

  if (loading) {
    return (
      <section style={{ 
        background: 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)', 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTopColor: '#2C7A7B',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: 'white' }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </section>
    );
  }

  if (slides.length === 0) {
    return (
      <section style={{ 
        background: 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)', 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2>Welcome to INTELIHEADS</h2>
          <p>No hero slides configured. Please add slides in admin panel.</p>
        </div>
      </section>
    );
  }

  const currentSlide = slides[currentIndex];
  const slideImage = getFullImageUrl(currentSlide.imageUrl || currentSlide.backgroundImage);
  const hasImage = slideImage && slideImage !== '';

  return (
    <section className="hero-section" style={{
      position: 'relative',
      height: '100vh',
      minHeight: '600px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      backgroundImage: hasImage ? `url(${slideImage})` : currentSlide.backgroundColor || 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      transition: 'background-image 0.5s ease-in-out'
    }}>
      {/* Overlay for better text readability */}
      {hasImage && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: currentSlide.overlayColor || 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 100%)',
          zIndex: 1
        }} />
      )}
      
      {/* Content Container */}
      <div className="container" style={{ position: 'relative', zIndex: 2, width: '100%', padding: '0 2rem' }}>
        <div style={{ 
          maxWidth: '900px', 
          margin: '0 auto', 
          textAlign: 'center',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          {/* Badge/Subtitle */}
          {currentSlide.subtitle && (
            <div style={{
              background: 'rgba(44,122,123,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'inline-block',
              padding: '0.5rem 1.5rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#4A9D9E',
              marginBottom: '1.5rem',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {currentSlide.subtitle}
            </div>
          )}
          
          {/* Title */}
          <h1 style={{
            fontSize: 'clamp(2rem, 8vw, 4rem)',
            lineHeight: '1.2',
            marginBottom: '1.5rem',
            color: currentSlide.textColor || 'white',
            fontWeight: 800,
            textShadow: hasImage ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
            letterSpacing: '-0.02em'
          }}>
            {currentSlide.title}
          </h1>
          
          {/* Description */}
          <p style={{
            fontSize: 'clamp(1rem, 4vw, 1.2rem)',
            color: currentSlide.textColor || 'rgba(255,255,255,0.95)',
            marginBottom: '2rem',
            lineHeight: '1.6',
            textShadow: hasImage ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
            maxWidth: '700px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {currentSlide.description}
          </p>
          
          {/* Button */}
          {currentSlide.buttonText && (
            <button
              onClick={() => {
                if (currentSlide.buttonLink === '#contact') {
                  onContactClick();
                } else if (currentSlide.buttonLink && currentSlide.buttonLink !== '#') {
                  window.location.href = currentSlide.buttonLink;
                }
              }}
              style={{
                background: '#2C7A7B',
                color: 'white',
                padding: '0.8rem 2rem',
                borderRadius: '50px',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#236B6C';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2C7A7B';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {currentSlide.buttonText}
              <i className="fas fa-arrow-right" />
            </button>
          )}
        </div>
      </div>

      {/* Left Arrow Button - Circular with Arrow Icon */}
      {slides.length > 1 && (
        <button
          onClick={prevSlide}
          className="slider-arrow-left"
          aria-label="Previous slide"
          style={{
            position: 'absolute',
            left: '30px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            border: '2px solid rgba(255,255,255,0.3)',
            borderRadius: '50%',
            width: '55px',
            height: '55px',
            cursor: 'pointer',
            fontSize: '1.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 10,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#2C7A7B';
            e.currentTarget.style.borderColor = 'white';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.6)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {/* Right Arrow Button - Circular with Arrow Icon */}
      {slides.length > 1 && (
        <button
          onClick={nextSlide}
          className="slider-arrow-right"
          aria-label="Next slide"
          style={{
            position: 'absolute',
            right: '30px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            border: '2px solid rgba(255,255,255,0.3)',
            borderRadius: '50%',
            width: '55px',
            height: '55px',
            cursor: 'pointer',
            fontSize: '1.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 10,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#2C7A7B';
            e.currentTarget.style.borderColor = 'white';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.6)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {/* Slide Indicators/Dots */}
      {slides.length > 1 && (
        <div className="slide-dots" style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '12px',
          zIndex: 10,
          padding: '8px 16px',
          borderRadius: '30px',
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(8px)'
        }}>
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`dot ${currentIndex === index ? 'active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
              style={{
                width: currentIndex === index ? '30px' : '8px',
                height: '8px',
                borderRadius: '4px',
                border: 'none',
                background: currentIndex === index ? '#2C7A7B' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (currentIndex !== index) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.8)';
                  e.currentTarget.style.transform = 'scale(1.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentIndex !== index) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.5)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .hero-section {
          animation: fadeIn 0.8s ease-out;
        }
        
        /* Responsive Arrow Buttons */
        @media (max-width: 992px) {
          .slider-arrow-left, .slider-arrow-right {
            width: 45px !important;
            height: 45px !important;
          }
          
          .slider-arrow-left svg, .slider-arrow-right svg {
            width: 20px !important;
            height: 20px !important;
          }
        }
        
        @media (max-width: 768px) {
          .slider-arrow-left, .slider-arrow-right {
            width: 40px !important;
            height: 40px !important;
          }
          
          .slider-arrow-left {
            left: 15px !important;
          }
          
          .slider-arrow-right {
            right: 15px !important;
          }
          
          .slider-arrow-left svg, .slider-arrow-right svg {
            width: 16px !important;
            height: 16px !important;
          }
          
          .slide-dots {
            bottom: 20px !important;
            gap: 8px !important;
            padding: 6px 12px !important;
          }
          
          .dot {
            width: 6px !important;
            height: 6px !important;
          }
          
          .dot.active {
            width: 20px !important;
          }
        }
        
        @media (max-width: 576px) {
          .slider-arrow-left, .slider-arrow-right {
            width: 35px !important;
            height: 35px !important;
          }
          
          .slider-arrow-left svg, .slider-arrow-right svg {
            width: 14px !important;
            height: 14px !important;
          }
        }
        
        /* Touch device optimization */
        @media (hover: none) and (pointer: coarse) {
          .slider-arrow-left, .slider-arrow-right {
            width: 48px !important;
            height: 48px !important;
            opacity: 0.9 !important;
            background: rgba(0,0,0,0.7) !important;
          }
        }
      `}</style>
    </section>
  );
};

export default DynamicHero;